import * as React from 'react';

import { PlanType, TeamRole } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { atom, useAtom, useSetAtom } from 'jotai';

import { ContactSalesEventAdapter } from '~analytics/adapters/ContactSalesEventAdapter';
import {
  CreatePortalSessionEventAdapter,
  CreatePortalSessionEventData,
} from '~analytics/adapters/CreatePortalSessionEventAdapter';
import {
  PricingTableSource,
  ViewPricingTableEventAdapter,
} from '~analytics/adapters/ViewPricingTableEventAdapter';
import {
  SmallButton,
  SmallButtonAppearance,
  SmallLinkButton,
} from '~shared/components/buttons';
import { SubscriptionState } from '~teacher/components/Account/helpers';
import { isConfirmLeaveTeamDialogOpenAtom } from '~teacher/components/Account/TeamMembers/ConfirmLeaveTeamDialog';
import { useTypeFormLink } from '~teacher/components/PricingTable/hooks';

import platform from '../../../../../../../shared/Platform';
import { createCustomerPortalSessionUrl } from '../../../../api/sales/salesUrls';
import { assertUnreachable } from '../../../../util/assertUnreachable';

const isUpgradingAtom = atom(false);

interface TeamSubscriptionControlsProps {
  teamRole?: TeamRole;
  planType: PlanType;
  subscriptionState?: SubscriptionState;
  teamId?: string;
  canOpenStripePortal: boolean;
  canOpenStripeCheckout: boolean;
  canViewPlans: boolean;
}

function PricingTableButton({
  children,
  source,
  appearance,
}: {
  appearance: SmallButtonAppearance;
  source: PricingTableSource;
  children: React.ReactNode;
}) {
  return (
    <div className="text-center w-70 md:w-full">
      <SmallLinkButton
        href="#pricing"
        appearance={appearance}
        onClick={() =>
          new ViewPricingTableEventAdapter({
            source: source,
          }).sendAnalytics()
        }
      >
        {children}
      </SmallLinkButton>
    </div>
  );
}

function ContactSalesButton() {
  const typeFormLink = useTypeFormLink();

  return (
    <div className="grid w-70 md:w-full">
      <SmallLinkButton
        href={typeFormLink.data}
        target="_blank"
        appearance="primary"
        className={'w-full flex justify-center'}
        onClick={() => {
          new ContactSalesEventAdapter({
            form_source: 'typeform',
          }).sendAnalytics();
        }}
      >
        {translate('Contact Sales')}
      </SmallLinkButton>
    </div>
  );
}

export function TeamSubscriptionControls({
  teamRole = TeamRole.Owner,
  subscriptionState,
  planType,
  teamId,
  canOpenStripePortal,
  canOpenStripeCheckout,
  canViewPlans,
}: TeamSubscriptionControlsProps) {
  return (
    <>
      <ControlButtonWrapper>
        {isB2B(planType) ? <ContactSalesButton /> : null}

        {!platform.isWrapperApp() &&
        canOpenStripePortal &&
        !isB2B(planType) &&
        isActive(subscriptionState) ? (
          <RenewManageSubscriptionButton
            subscriptionState={subscriptionState}
            teamId={teamId}
          />
        ) : null}

        {canViewPlans && isActive(subscriptionState) ? (
          <PricingTableButton
            appearance="default"
            source={'account_page__view_plans'}
          >
            {translate('View Plans')}
          </PricingTableButton>
        ) : null}

        {!isB2B(planType) && canViewPlans && isExpired(subscriptionState) ? (
          <PricingTableButton
            appearance="primary"
            source={'account_page_teams__renew'}
          >
            {translate('Renew')}
          </PricingTableButton>
        ) : null}

        {canViewPlans && isFree(planType) ? (
          <PricingTableButton
            appearance="primary"
            source={'account_page_teams__upgrade'}
          >
            {translate('Upgrade')}
          </PricingTableButton>
        ) : null}

        {!platform.isWrapperApp() &&
        canOpenStripeCheckout &&
        !isExpired(subscriptionState) &&
        isEssentials(planType) ? (
          <UpgradeToProButton teamId={teamId} />
        ) : null}

        {!isB2B(planType) ? <EnterLicenseKeyButton /> : null}

        {teamRole !== TeamRole.Owner ? <LeaveTeamButton /> : null}
      </ControlButtonWrapper>
    </>
  );
}

function getButtonTextAndSource(subscriptionState: SubscriptionState): {
  buttonText: string;
  source: CreatePortalSessionEventData['source'];
} {
  switch (subscriptionState) {
    case 'Expired':
    case 'CancellingTrial':
    case 'Cancelling':
      return {
        buttonText: translate('Renew') as string,
        source: 'account_page_teams__renew' as const,
      };
    case 'Trial':
    case 'Valid':
    case 'PastDue':
      return {
        buttonText: translate('Manage Subscription') as string,
        source: 'account_page_teams__manage' as const,
      };
    case 'None':
      return {
        buttonText: translate('Upgrade') as string,
        source: 'account_page_teams__upgrade' as const,
      };
    default: {
      if (__DEV__) {
        assertUnreachable(subscriptionState);
      }
      return {
        buttonText: '',
        source: 'account_page_teams__upgrade' as const,
      };
    }
  }
}

function RenewManageSubscriptionButton({
  subscriptionState,
  teamId,
}: {
  subscriptionState: SubscriptionState;
  teamId: string;
}) {
  const [isUpgrading, setIsUpgrading] = useAtom(isUpgradingAtom);
  const { source, buttonText } = getButtonTextAndSource(subscriptionState);

  return (
    <CustomerPortalSessionForm
      source={source}
      teamId={teamId}
      onSubmit={() => setIsUpgrading(true)}
    >
      <SmallButton
        isLoading={isUpgrading}
        type="submit"
        appearance="primary"
        className="whitespace-no-wrap" //TODO: change to whitespace-nowrap when upgrading to tailwind 2
      >
        {buttonText}
      </SmallButton>
      <input name="team_id" id="team_id" value={teamId} readOnly hidden />
    </CustomerPortalSessionForm>
  );
}

function UpgradeToProButton({ teamId }: { teamId?: string }) {
  const [isUpgrading, setIsUpgrading] = useAtom(isUpgradingAtom);

  return (
    <CustomerPortalSessionForm
      className="grid"
      onSubmit={() => setIsUpgrading(true)}
      source="account_page_teams__upgrade"
      teamId={teamId}
    >
      <input name="action" id="action" value="switch_plan" readOnly hidden />
      <input name="team_id" id="team_id" value={teamId} readOnly hidden />

      <SmallButton
        className="whitespace-no-wrap w-70 md:w-full"
        isLoading={isUpgrading}
        type="submit"
      >
        {translate('Upgrade to Pro')}
      </SmallButton>
    </CustomerPortalSessionForm>
  );
}

function LeaveTeamButton() {
  const setIsConfirmTeamDialogOpen = useSetAtom(
    isConfirmLeaveTeamDialogOpenAtom
  );

  return (
    <SmallButton
      className="whitespace-no-wrap w-70 md:w-full"
      appearance="text"
      onClick={() => setIsConfirmTeamDialogOpen(true)}
    >
      {translate('Leave Team')}
    </SmallButton>
  );
}

function EnterLicenseKeyButton() {
  return (
    <div className="text-center whitespace-no-wrap w-70 md:w-full">
      <SmallLinkButton href="#profile/apply" appearance={'text'}>
        {translate('Enter License Key')}
      </SmallLinkButton>
    </div>
  );
}

interface CustomerPortalSessionFormProps
  extends React.HTMLProps<HTMLFormElement> {
  source: CreatePortalSessionEventData['source'];
  teamId: string;
  children: React.ReactNode;
  onSubmit?: () => void;
}

function CustomerPortalSessionForm({
  children,
  onSubmit,
  source,
  teamId,
  ...props
}: CustomerPortalSessionFormProps) {
  const customerPortalSessionUrl = createCustomerPortalSessionUrl().href;
  return (
    <form
      className="grid w-70 md:w-full"
      method="post"
      action={customerPortalSessionUrl}
      onSubmit={() => {
        onSubmit?.();
        new CreatePortalSessionEventAdapter({
          source: source,
          team_id: teamId,
        }).sendAnalytics();
      }}
      {...props}
    >
      {children}
    </form>
  );
}

function ControlButtonWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid content-start auto-rows-min gap-2 whitespace-no-wrap">
      {children}
    </div>
  );
}

function isB2B(plan: PlanType) {
  return [PlanType.Complete, PlanType.Organization].includes(plan);
}

function isExpired(subscriptionState: SubscriptionState) {
  return subscriptionState === 'Expired';
}

function isActive(subscriptionState: SubscriptionState) {
  return [
    'Cancelling',
    'Valid',
    'CancellingTrial',
    'Trial',
    'PastDue', // NOTE: In past due the user does not have PRO, but they should manage their plan to fix payment
  ].includes(subscriptionState);
}

function isFree(planType: PlanType) {
  return planType === PlanType.Free || planType === PlanType.FreeLegacy;
}
function isEssentials(planType: PlanType) {
  return planType === PlanType.Essentials || planType == PlanType.Starter;
}
